import { Button, Flex, Input, Table, Typography, message } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  addCommunityReply,
  deleteCommunityComment,
  deleteCommunityReply,
  getCommunityCommentList,
} from '../../api/community.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/sales.module.scss'

const { Title, Text, Paragraph } = Typography

const CommunityCommentPage = () => {
  const { categoryId } = useParams()
  const communityId = categoryId

  const [comments, setComments] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalElements, setTotalElements] = useState(0)

  const [selectedCommentId, setSelectedCommentId] = useState(null)
  const [replyInput, setReplyInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchComments = async (pageArg = page, sizeArg = pageSize) => {
    if (!communityId) return
    setIsLoading(true)
    try {
      const res = await getCommunityCommentList(communityId, pageArg, sizeArg)
      const list = res?.content ?? []
      setComments(list)
      setTotalElements(res?.totalElements ?? 0)
      if (list.length > 0 && !selectedCommentId) {
        setSelectedCommentId(list[0].id)
      }
    } catch (error) {
      console.error('getCommunityCommentList error', error)
      setComments([])
      setTotalElements(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchComments(page, pageSize)
    // eslint-disable-next-line
  }, [communityId, page, pageSize])

  const selectedComment = useMemo(
    () => comments.find((c) => c.id === selectedCommentId) || null,
    [comments, selectedCommentId],
  )
  const columns = [
    {
      title: '작성자 ID',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      width: 80,
      align: 'center',
    },
    {
      title: '작성자',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      align: 'center',
    },
    {
      title: '내용',
      dataIndex: 'content',
      key: 'content',
      render: (text) => (
        <span style={{ fontSize: 14 }}>
          {typeof text === 'string' && text.length > 100
            ? `${text.slice(0, 100)}...`
            : text || '-'}
        </span>
      ),
    },
    {
      title: '답변 완료',
      // dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (id, record) => (
        <span style={{ fontSize: 14 }}>
          {record.replies.filter((r) => r.createdBy === 1).length > 0
            ? '완료'
            : '미완료'}
        </span>
      ),
    },
    {
      title: '체험단 참여 여부',
      dataIndex: 'participant',
      key: 'participant',
      width: 120,
      align: 'center',
      render: (id, record) => (
        <span style={{ fontSize: 14 }}>
          {record.participant ? '참여' : '미참여'}
        </span>
      ),
    },
    {
      title: '등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center',
      render: (date) => <span style={{ fontSize: 12 }}>{date}</span>,
    },
  ]

  const handleSubmitReply = async () => {
    if (!selectedComment) {
      message.warning('댓글을 먼저 선택해주세요.')
      return
    }
    const trimmed = (replyInput || '').trim()
    if (!trimmed) {
      message.warning('답글 내용을 입력해주세요.')
      return
    }
    setIsSubmitting(true)
    try {
      await addCommunityReply({
        commentId: selectedComment.id,
        content: trimmed,
        userId: 1,
      })
      message.success('답글이 등록되었습니다.')
      setReplyInput('')
      await fetchComments()
      setSelectedCommentId(selectedComment.id)
    } catch (e) {
      console.error('addCommunityReply error', e)
      const fallback =
        e?.response?.data?.detail?.[0]?.description ||
        '답글 등록에 실패했습니다.'
      message.error(fallback)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId) => {
    Swal.fire({
      text: '댓글을 삭제하시겠습니까?',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteCommunityComment(commentId)
        message.success('댓글이 삭제되었습니다.')
        await fetchComments()
      }
    })
  }
  const handleDeleteReply = async (replyId) => {
    console.log('replyId', replyId)
    Swal.fire({
      text: '관리자 답글을 삭제하시겠습니까?',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteCommunityReply(replyId)
        message.success('관리자 답글이 삭제되었습니다.')
        await fetchComments()
      }
    })
  }

  return (
    <>
      <ContentHeader title="커뮤니티 댓글 관리" />
      <div style={{ padding: '0 24px 60px' }}>
        <Flex
          justify="space-between"
          align="center"
          style={{
            background: '#f8fafd',
            borderRadius: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            padding: '18px 24px',
            marginTop: 24,
            marginBottom: 18,
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>
              💬 댓글 목록
            </Title>
          </div>
          <span style={{ fontWeight: 600, fontSize: 18, color: '#1677ff' }}>
            총{' '}
            <span style={{ fontWeight: 'bold', fontSize: 22 }}>
              {totalElements}
            </span>{' '}
            건
          </span>
        </Flex>
        <div
          className={`${styles['product-register-border-box']}`}
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={comments}
            rowKey="id"
            columns={columns}
            loading={isLoading}
            size="small"
            pagination={{
              current: page + 1,
              pageSize: pageSize,
              total: totalElements,
              onChange: (p, ps) => {
                setPage(p - 1)
                setPageSize(ps)
              },
              showSizeChanger: true,
            }}
            onRow={(record) => ({
              onClick: () => setSelectedCommentId(record.id),
              style: {
                cursor: 'pointer',
                backgroundColor:
                  record.id === selectedCommentId ? '#f0f7ff' : undefined,
              },
            })}
            bordered
          />
        </div>
        <div
          className={`${styles['product-register-border-box']}`}
          style={{
            minHeight: 420,
            background: '#fff',
            padding: '16px',
            borderRadius: 10,
          }}
        >
          {selectedComment ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div
                style={{
                  padding: '12px 14px',
                  background: '#E5EFFF',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  border: '1px solid #1677ff',
                  // alignItems: 'center',
                }}
              >
                <div>
                  <Text strong>{selectedComment.username}</Text>
                  <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                    {selectedComment.createdAt}
                  </span>
                  <Paragraph
                    style={{
                      marginTop: 8,
                      marginBottom: 0,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedComment.content}
                  </Paragraph>
                </div>
                <Button
                  type="default"
                  onClick={() => handleDeleteComment(selectedComment.id)}
                  loading={isSubmitting}
                >
                  삭제
                </Button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(selectedComment.replies || []).length === 0 ? (
                  <Text type="secondary">답글이 없습니다.</Text>
                ) : (
                  <>
                    <p
                      style={{
                        marginTop: 20,
                        marginBottom: 8,
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      ❤️ 댓글 관리 ❤️
                    </p>
                    {(selectedComment.replies || []).map((reply) => (
                      <div
                        key={reply.id}
                        style={{
                          background:
                            reply.createdBy === 1 ? '#F0FCE1' : '#fff',
                          border: '1px solid #efefef',
                          borderRadius: 8,
                          padding: '10px 12px',
                          position: 'relative',
                        }}
                      >
                        <Text strong>
                          {`${reply.username} (USERID: ${reply.createdBy})`}
                        </Text>
                        <span
                          style={{ marginLeft: 8, color: '#888', fontSize: 12 }}
                        >
                          {reply.updatedAt}
                        </span>
                        <Paragraph
                          style={{
                            marginTop: 6,
                            marginBottom: 0,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {reply.content}
                        </Paragraph>

                        <Button
                          type="link"
                          onClick={() => handleDeleteReply(reply.id)}
                          loading={isSubmitting}
                          style={{
                            fontSize: 14,
                            position: 'absolute',
                            right: 0,
                            bottom: 6,
                            color: '#888',
                          }}
                        >
                          삭제
                        </Button>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Input.TextArea
                  value={replyInput}
                  onChange={(e) => setReplyInput(e.target.value)}
                  placeholder="답글을 입력하세요"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  maxLength={500}
                />
                <Button
                  type="primary"
                  onClick={handleSubmitReply}
                  loading={isSubmitting}
                >
                  등록
                </Button>
              </div>
            </div>
          ) : (
            <Text type="secondary"> 목록에서 댓글을 선택하세요.</Text>
          )}
        </div>
      </div>
    </>
  )
}

export default CommunityCommentPage
